<div class="space-y-6">

    <!-- Header -->
    <div class="flex items-center justify-between flex-wrap gap-3">
        <div class="space-y-1">
            <h2 class="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight">
                Tools &amp; Integrations
            </h2>
            <p class="text-sm text-gray-500 dark:text-gray-400">
                Connect external tools and integrations to power your chatbot
            </p>
        </div>
        <button type="button"
            class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 active:bg-violet-800 text-white text-sm font-semibold shadow-md shadow-violet-500/25 transition-all duration-150"
            (click)="onCreateTool()">
            <app-icon-v2 name="Plus" [size]="16" />
            Create Tool
        </button>
    </div>

    <!-- Stats row -->
    @if (!loading() && tools().length > 0) {
    <div class="grid grid-cols-3 gap-3">
        <div class="rounded-xl border border-gray-200/80 dark:border-gray-700/60 bg-white dark:bg-gray-900/60 px-4 py-3 flex items-center gap-3">
            <div class="flex items-center justify-center w-9 h-9 rounded-lg bg-violet-50 dark:bg-violet-950/50 shrink-0">
                <app-icon-v2 name="Wrench" [size]="18" [customClass]="'text-violet-600 dark:text-violet-400'" />
            </div>
            <div>
                <p class="text-lg font-bold text-gray-900 dark:text-white leading-tight">{{ tools().length }}</p>
                <p class="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">Total tools</p>
            </div>
        </div>
        <div class="rounded-xl border border-gray-200/80 dark:border-gray-700/60 bg-white dark:bg-gray-900/60 px-4 py-3 flex items-center gap-3">
            <div class="flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 shrink-0">
                <app-icon-v2 name="Database" [size]="18" [customClass]="'text-emerald-600 dark:text-emerald-400'" />
            </div>
            <div>
                <p class="text-lg font-bold text-gray-900 dark:text-white leading-tight">{{ dbCount() }}</p>
                <p class="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">Database</p>
            </div>
        </div>
        <div class="rounded-xl border border-gray-200/80 dark:border-gray-700/60 bg-white dark:bg-gray-900/60 px-4 py-3 flex items-center gap-3">
            <div class="flex items-center justify-center w-9 h-9 rounded-lg bg-sky-50 dark:bg-sky-950/50 shrink-0">
                <app-icon-v2 name="Globe" [size]="18" [customClass]="'text-sky-600 dark:text-sky-400'" />
            </div>
            <div>
                <p class="text-lg font-bold text-gray-900 dark:text-white leading-tight">{{ apiCount() }}</p>
                <p class="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">API</p>
            </div>
        </div>
    </div>
    }

    <!-- Search + Filter -->
    @if (!loading() && tools().length > 0) {
    <div class="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div class="relative flex-1 min-w-0 w-full">
            <span class="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center">
                <app-icon-v2 name="Search" [size]="15" [customClass]="'text-gray-400 dark:text-gray-500'" />
            </span>
            <input type="text" [value]="searchQuery()" (input)="searchQuery.set($any($event.target).value)"
                placeholder="Search tools by name or description…"
                class="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500/40 focus:border-violet-400 transition-colors" />
        </div>
        <div class="flex items-center gap-1.5 shrink-0">
            <button type="button" class="px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors"
                [ngClass]="filterType() === 'ALL' ? 'bg-violet-600 border-violet-600 text-white' : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'"
                (click)="setFilter('ALL')">All</button>
            <button type="button" class="px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors"
                [ngClass]="filterType() === 'DATABASE' ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'"
                (click)="setFilter('DATABASE')">Database</button>
            <button type="button" class="px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors"
                [ngClass]="filterType() === 'API' ? 'bg-sky-600 border-sky-600 text-white' : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'"
                (click)="setFilter('API')">API</button>
        </div>
    </div>
    }

    <!-- Loading skeleton -->
    @if (loading()) {
    <div class="grid grid-cols-1 gap-3">
        @for (i of [1,2,3,4,5]; track i) {
        <div class="rounded-xl border border-gray-200/80 dark:border-gray-700/60 bg-white dark:bg-gray-900/60 p-4 animate-pulse">
            <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-700 shrink-0"></div>
                <div class="flex-1 space-y-2">
                    <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                    <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-2/3"></div>
                </div>
            </div>
        </div>
        }
    </div>
    }

    <!-- Error state -->
    @if (error()) {
    <div class="rounded-xl border border-red-200 dark:border-red-800/60 bg-red-50 dark:bg-red-950/30 px-4 py-5 flex items-center gap-3">
        <app-icon-v2 name="AlertCircle" [size]="20" [customClass]="'text-red-500 dark:text-red-400 shrink-0'" />
        <p class="text-sm text-red-700 dark:text-red-300">{{ error() }}</p>
    </div>
    }

    <!-- Empty state -->
    @if (!loading() && !error() && tools().length === 0) {
    <div class="relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-800 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 p-10 w-full min-h-[240px] flex items-center justify-center">
        <div class="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_top_right,_rgba(139,92,246,0.18),_transparent_42%)]"></div>
        <div class="relative flex flex-col items-center text-center gap-5">
            <div class="flex items-center justify-center p-4 rounded-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur border border-gray-200 dark:border-gray-700 shadow-sm">
                <app-icon-v2 name="Wrench" [size]="26" [customClass]="'text-violet-600 dark:text-violet-400'" />
            </div>
            <div class="space-y-1">
                <p class="text-lg font-semibold text-gray-800 dark:text-gray-200">No tools configured yet</p>
                <p class="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                    Create your first tool to let the chatbot query databases or call external APIs.
                </p>
            </div>
            <button type="button"
                class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold shadow-md shadow-violet-500/20 transition-all duration-150"
                (click)="onCreateTool()">
                <app-icon-v2 name="Plus" [size]="15" />
                Create your first tool
            </button>
        </div>
    </div>
    }

    <!-- No results -->
    @if (!loading() && !error() && tools().length > 0 && filteredTools().length === 0) {
    <div class="rounded-xl border border-gray-200/80 dark:border-gray-700/60 bg-white/60 dark:bg-gray-900/40 px-6 py-10 flex flex-col items-center gap-3 text-center">
        <app-icon-v2 name="Search" [size]="28" [customClass]="'text-gray-300 dark:text-gray-600'" />
        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">No tools match your search</p>
        <p class="text-xs text-gray-500 dark:text-gray-400">Try a different keyword or change the filter.</p>
    </div>
    }

    <!-- Tools list -->
    @if (!loading() && !error() && filteredTools().length > 0) {
    <div class="flex flex-col gap-2">
        @for (tool of filteredTools(); track tool.pid) {
        <div class="rounded-xl border border-gray-200/80 dark:border-gray-700/60 bg-white dark:bg-gray-900/60 overflow-hidden transition-shadow duration-150 hover:shadow-md dark:hover:shadow-black/30">
            <button type="button" class="w-full flex items-start gap-4 px-4 py-3.5 text-left" (click)="toggleExpand(tool.pid)">
                <div class="shrink-0 mt-0.5">
                    @if (tool.toolType === 'DATABASE') {
                    <div class="flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200/70 dark:border-emerald-800/60">
                        <app-icon-v2 name="Database" [size]="17" [customClass]="'text-emerald-600 dark:text-emerald-400'" />
                    </div>
                    } @else {
                    <div class="flex items-center justify-center w-9 h-9 rounded-lg bg-sky-50 dark:bg-sky-950/60 border border-sky-200/70 dark:border-sky-800/60">
                        <app-icon-v2 name="Globe" [size]="17" [customClass]="'text-sky-600 dark:text-sky-400'" />
                    </div>
                    }
                </div>
                <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                        <span class="text-sm font-semibold text-gray-900 dark:text-white">{{ tool.name }}</span>
                        @if (tool.toolType === 'DATABASE') {
                        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200/70 dark:border-emerald-800/60">
                            <app-icon-v2 name="Database" [size]="10" />DATABASE
                        </span>
                        } @else {
                        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-100 dark:bg-sky-950/60 text-sky-700 dark:text-sky-300 border border-sky-200/70 dark:border-sky-800/60">
                            <app-icon-v2 name="Globe" [size]="10" />API · {{ tool.apiMethod }}
                        </span>
                        }
                        @if (tool.isGlobal) {
                        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-100 dark:bg-violet-950/60 text-violet-700 dark:text-violet-300 border border-violet-200/70 dark:border-violet-800/60">Global</span>
                        }
                    </div>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">{{ tool.description }}</p>
                </div>
                <div class="flex items-center gap-2">
                    <button type="button" class="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 hover:text-violet-600 dark:hover:text-white transition-colors" (click)="onEditTool(tool, $event)" title="Edit Tool">
                        <app-icon-v2 name="pencil" [size]="15" />
                    </button>
                    <button type="button" class="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-950/40 text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors" (click)="deleteTool(tool, $event)" title="Delete Tool">
                        <app-icon-v2 name="trash" [size]="15" />
                    </button>
                    <app-icon-v2 [name]="expandedPid() === tool.pid ? 'ChevronUp' : 'ChevronDown'" [size]="16" [customClass]="'text-gray-400 dark:text-gray-500 shrink-0'" />
                </div>
            </button>

            @if (expandedPid() === tool.pid) {
            <div class="border-t border-gray-100 dark:border-gray-800 px-4 py-4 flex flex-col gap-4 bg-gray-50/50 dark:bg-gray-950/30">
                <div class="flex flex-col gap-1">
                    <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Description</p>
                    <p class="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{{ tool.description }}</p>
                </div>
                @if (tool.guardrails) {
                <div class="flex flex-col gap-1">
                    <div class="flex items-center gap-1.5">
                        <app-icon-v2 name="ShieldAlert" [size]="12" [customClass]="'text-amber-500'" />
                        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Guardrails</p>
                    </div>
                    <p class="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 border border-amber-200/70 dark:border-amber-800/50 rounded-lg px-3 py-2">{{ tool.guardrails }}</p>
                </div>
                }
                @if (tool.toolType === 'DATABASE' && tool.mysqlQuery) {
                <div class="flex flex-col gap-1">
                    <div class="flex items-center gap-1.5">
                        <app-icon-v2 name="Code2" [size]="12" [customClass]="'text-gray-400'" />
                        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">MySQL Query</p>
                    </div>
                    <pre class="text-xs text-gray-800 dark:text-gray-200 bg-gray-900/5 dark:bg-gray-950/60 border border-gray-200/80 dark:border-gray-700/60 rounded-lg px-3 py-2.5 overflow-x-auto whitespace-pre-wrap font-mono leading-relaxed">{{ tool.mysqlQuery }}</pre>
                </div>
                }
                @if (tool.toolType === 'API') {
                <div class="flex flex-col gap-3">
                    @if (tool.apiUrl) {
                    <div class="flex flex-col gap-1">
                        <div class="flex items-center gap-1.5">
                            <app-icon-v2 name="Link" [size]="12" [customClass]="'text-gray-400'" />
                            <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Endpoint</p>
                        </div>
                        <div class="flex items-center gap-2">
                            <span class="shrink-0 px-2 py-0.5 rounded-md text-[11px] font-bold bg-sky-100 dark:bg-sky-950/60 text-sky-700 dark:text-sky-300 border border-sky-200/60 dark:border-sky-800/50">{{ tool.apiMethod ?? 'GET' }}</span>
                            <code class="text-xs text-gray-700 dark:text-gray-300 font-mono truncate">{{ tool.apiUrl }}</code>
                        </div>
                    </div>
                    }
                    @if (tool.apiHeaders && (tool.apiHeaders | json) !== '{}') {
                    <div class="flex flex-col gap-1">
                        <div class="flex items-center gap-1.5">
                            <app-icon-v2 name="FileJson" [size]="12" [customClass]="'text-gray-400'" />
                            <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Headers</p>
                        </div>
                        <pre class="text-xs text-gray-800 dark:text-gray-200 bg-gray-900/5 dark:bg-gray-950/60 border border-gray-200/80 dark:border-gray-700/60 rounded-lg px-3 py-2.5 overflow-x-auto font-mono leading-relaxed">{{ tool.apiHeaders | json }}</pre>
                    </div>
                    }
                </div>
                }
            </div>
            }
        </div>
        }
    </div>
    }

</div>